import { JSONContent, Editor } from "@tiptap/react";
import { create } from "zustand";

import { CONTENT_RESET_META } from "@demo/extensions/utils/node-protection";
import { Role } from "@demo/types/base";

export interface DocumentEditorRef {
  editor: Editor | null;
  clear: () => void;
  focus: () => void;
  getEditor: () => Editor | null;
  handleFileDrop: (files: File[]) => void;
}

interface EditorRefStore {
  userEditorRef: DocumentEditorRef | null;
  systemEditorRef: DocumentEditorRef | null;

  // Whether each editor's content differs from the last saved/run content.
  // Written by useEditorDirtyState; read by any surface that needs to show a
  // sync/unsaved indicator (e.g. the playground).
  isUserEditorContentDirty: boolean;
  isSystemEditorContentDirty: boolean;

  setEditorRef: (role: Role, ref: DocumentEditorRef | null) => void;
  setEditorDirtyState: (role: Role, isDirty: boolean) => void;

  getEditorContentAsJSON: (role: Role) => JSONContent;
  setEditorContentFromJSON: (role: Role, json: JSONContent) => void;
}

export const useEditorRefStore = create<EditorRefStore>((set, _get) => ({
  userEditorRef: null,
  systemEditorRef: null,
  isUserEditorContentDirty: false,
  isSystemEditorContentDirty: false,

  setEditorRef: (role: Role, ref: DocumentEditorRef | null) =>
    set((state) => ({
      ...state,
      userEditorRef: role === Role.USER ? ref : state.userEditorRef,
      systemEditorRef: role === Role.SYSTEM ? ref : state.systemEditorRef,
    })),

  setEditorDirtyState: (role: Role, isDirty: boolean) =>
    set((state) => ({
      ...state,
      isUserEditorContentDirty: role === Role.USER ? isDirty : state.isUserEditorContentDirty,
      isSystemEditorContentDirty: role === Role.SYSTEM ? isDirty : state.isSystemEditorContentDirty,
    })),

  getEditorContentAsJSON: (role: Role): JSONContent => {
    const editor = getEditorRef(role)?.editor;
    if (!editor) {
      return {
        type: "doc",
        content: [{ type: "paragraph" }],
      }; // fallback blank doc
    }
    return editor.getJSON();
  },

  setEditorContentFromJSON: (role: Role, json: JSONContent) => {
    const editor = getEditorRef(role)?.editor;
    if (editor) {
      // Defer content update to avoid flushSync during render
      queueMicrotask(() => {
        if (!editor.isDestroyed) {
          // contentReset exempts this programmatic full-doc swap (clear /
          // template load) from node protection; addToHistory keeps it off
          // the user's undo stack.
          editor.chain().setMeta(CONTENT_RESET_META, true).setMeta("addToHistory", false).setContent(json).run();
        }
      });
    }
  },
}));

const getEditorRef = (role: Role) =>
  role === Role.USER ? useEditorRefStore.getState().userEditorRef : useEditorRefStore.getState().systemEditorRef;
